import React, { useState } from "react";
import { ChatTabsDesktop } from "@/components/chat/ChatTabsDesktop";
import { ChatTabsMobile } from "@/components/chat/ChatTabsMobile";
import { NotebookPen, Copy, FileDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNotesEditor } from "@/context/NotesEditorContext";
import { getNotesAsText, exportNotesAsPDF } from "@/lib/notes/exportNotes";
import { NotesPanel } from "@/components/chat/panels/NotesPanel";
import { ChaptersPanel } from "@/components/chat/panels/ChaptersPanel";
import { ContentChatPanel } from "./panels/ContentChatPanel";
import { NotesFloater } from "@/components/notes/NotesFloater";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { NotesEditorProvider } from "@/context/NotesEditorContext";
import { ContentProvider } from "./ContentContext";
import { CONTENT_CHAT_TABS } from "./contentChatConfig";
import { ContentFlashcardsPanel } from "./panels/ContentFlashcardsPanel";
import { ContentQuizzesPanel } from "./panels/ContentQuizzesPanel";
import { ContentSummaryPanel } from "./panels/ContentSummaryPanel";
import { usePublicAuth } from "@/context/PublicAuthContext";
import type { ContentItem } from "@/types/content";
import type { ChatTabId } from "@/components/chat/ChatRoot";

interface ContentChatRootProps {
  content: ContentItem;
  isContentViewerVisible?: boolean;
}

export function ContentChatRoot({ content, isContentViewerVisible = true }: ContentChatRootProps) {
  const allowedTabs = CONTENT_CHAT_TABS[content.type];
  const [activeTab, setActiveTab] = useState<ChatTabId>(allowedTabs?.[0] || "chat");
  const [isFloaterOpen, setIsFloaterOpen] = useState(false);
  const { toast } = useToast();

  return (
    <ContentProvider content={content}>
      <NotesEditorProvider>
        <ContentChatRootInner
          content={content}
          allowedTabs={allowedTabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          isFloaterOpen={isFloaterOpen}
          setIsFloaterOpen={setIsFloaterOpen}
          toast={toast}
          isContentViewerVisible={isContentViewerVisible}
        />
      </NotesEditorProvider>
    </ContentProvider>
  );
}

interface ContentChatRootInnerProps {
  content: ContentItem;
  allowedTabs: ChatTabId[];
  activeTab: ChatTabId;
  setActiveTab: (tab: ChatTabId) => void;
  isFloaterOpen: boolean;
  setIsFloaterOpen: (open: boolean) => void;
  toast: ReturnType<typeof useToast>["toast"];
  isContentViewerVisible: boolean;
}

function ContentChatRootInner({
  content,
  allowedTabs,
  activeTab,
  setActiveTab,
  isFloaterOpen,
  setIsFloaterOpen,
  toast,
  isContentViewerVisible,
}: ContentChatRootInnerProps) {
  const { editor } = useNotesEditor();
  const { requireAuth, isPublicView } = usePublicAuth();

  const handleCopyNotes = async () => {
    if (isPublicView) { requireAuth(); return; }
    if (!editor) {
      toast({
        title: "Error",
        description: "Notes editor not ready",
        variant: "destructive"
      });
      return;
    }
    try {
      const text = getNotesAsText(editor);
      if (!text.trim()) {
        toast({
          title: "No content",
          description: "There are no notes to copy",
          variant: "destructive"
        });
        return;
      }
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copied!",
        description: "Notes copied to clipboard"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to copy notes",
        variant: "destructive"
      });
    }
  };

  const handleExportPDF = async () => {
    if (isPublicView) { requireAuth(); return; }
    if (!editor) {
      toast({
        title: "Error",
        description: "Notes editor not ready",
        variant: "destructive"
      });
      return;
    }
    try {
      await exportNotesAsPDF(editor);
      toast({
        title: "Success!",
        description: "Notes exported as PDF"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to export PDF",
        variant: "destructive"
      });
    }
  };

  const handleOpenFloater = () => {
    if (isPublicView) { requireAuth(); return; }
    setIsFloaterOpen(true);
  };

  const handleTabChange = (tab: ChatTabId) => {
    if (isPublicView) { requireAuth(); return; }
    setActiveTab(tab);
  };

  const showFloaterButton = activeTab === "notes";

  return (
    <div className="relative flex h-full w-full flex-col">
      {/* Capture all clicks in public view */}
      {isPublicView && (
        <div 
          className="absolute inset-0 z-10 cursor-pointer" 
          onClick={requireAuth}
          aria-hidden="true"
        />
      )}
      {/* Notes Floater */}
      <NotesFloater
        isOpen={isFloaterOpen}
        onClose={() => setIsFloaterOpen(false)}
        title="Notes"
      />

      {/* Desktop */}
      <div className="hidden h-full w-full md:flex md:flex-col overflow-hidden">
        <div className="flex-shrink-0 flex justify-center pt-3">
          <ChatTabsDesktop activeTab={activeTab} setActiveTab={handleTabChange} allowedTabs={allowedTabs} className="mx-0 px-[6px]" />
        </div>

        {/* Header controls */}
        <div className="flex-shrink-0 flex justify-end px-6 pt-2">
          <TooltipProvider>
            <div className="flex items-center justify-end gap-2">
              {showFloaterButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleOpenFloater}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-auto border-none bg-background/80 px-2 py-1 text-sm text-muted-foreground backdrop-blur-sm"
                    >
                      <span className="flex items-center gap-1">
                        <NotebookPen className="h-4 w-4" />
                        <span>Notes Floater</span>
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open Notes Floater</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {activeTab === "notes" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleCopyNotes}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy Notes</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleExportPDF}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <FileDown className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Download Notes</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </TooltipProvider>
        </div>

        <div className="mt-1 flex-1 min-h-0 overflow-hidden">
          {activeTab === "chat" && (
            <ContentChatPanel 
              content={content} 
              onSwitchTab={handleTabChange}
            />
          )}
          {activeTab === "flashcards" && <ContentFlashcardsPanel contentId={content.id} chatId={content.id} />}
          {activeTab === "quizzes" && <ContentQuizzesPanel contentId={content.id} />}
          {activeTab === "chapters" && <ChaptersPanel />}
          {activeTab === "summary" && <ContentSummaryPanel contentId={content.id} />}
          <div className={activeTab === "notes" ? "h-full" : "hidden"}>
            <NotesPanel />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className={`flex ${isContentViewerVisible ? 'h-full' : 'h-[calc(100dvh-96px)]'} flex-col md:hidden`}>
        <div className="px-2 pt-2">
          <ChatTabsMobile activeTab={activeTab} setActiveTab={handleTabChange} allowedTabs={allowedTabs} />
        </div>

        {/* Header controls */}
        <div className="flex justify-end px-4 pt-2">
          <TooltipProvider>
            <div className="sticky top-0 z-20 flex items-center justify-end gap-2">
              {showFloaterButton && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleOpenFloater}
                      className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-auto border-none bg-background/80 px-2 py-1 text-sm text-muted-foreground backdrop-blur-sm"
                    >
                      <span className="flex items-center gap-1">
                        <NotebookPen className="h-4 w-4" />
                        <span>Notes Floater</span>
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open Notes Floater</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {activeTab === "notes" && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleCopyNotes}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Copy Notes</p>
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={handleExportPDF}
                        className="inline-flex items-center justify-center whitespace-nowrap rounded-lg font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 hover:bg-accent hover:text-accent-foreground h-7 w-7 border-none bg-background/80 text-xs text-muted-foreground backdrop-blur-sm"
                      >
                        <FileDown className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Download Notes</p>
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
          </TooltipProvider>
        </div>

        <div className="mt-1 flex-1 overflow-hidden">
          {activeTab === "chat" && (
            <ContentChatPanel 
              isMobile 
              content={content} 
              onSwitchTab={handleTabChange}
            />
          )}
          {activeTab === "flashcards" && <ContentFlashcardsPanel isMobile contentId={content.id} chatId={content.id} />}
          {activeTab === "quizzes" && <ContentQuizzesPanel isMobile contentId={content.id} />}
          {activeTab === "chapters" && <ChaptersPanel isMobile />}
          {activeTab === "summary" && <ContentSummaryPanel isMobile contentId={content.id} />}
          <div className={activeTab === "notes" ? "h-full" : "hidden"}>
            <NotesPanel isMobile />
          </div>
        </div>
      </div>
    </div>
  );
}
